// HELP, SRC and MOST.

import { get, type Quote } from "../api";
import { clear, errorBox, h, loading, toggles } from "../dom";
import * as f from "../fmt";
import { allFns, register, type Ctx, type Group } from "../registry";
import { DataTable } from "../table";

// --- HELP -------------------------------------------------------------------

const GROUPS: Group[] = ["Equity", "Markets", "News & Research", "Rates & Economy", "Tools"];

function runHelp(ctx: Ctx): void {
  const fns = allFns().sort((a, b) => a.code.localeCompare(b.code));
  const sections = GROUPS.map((g) => {
    const list = fns.filter((d) => d.group === g);
    if (!list.length) return null;
    return h(
      "section",
      null,
      h("h3", null, g),
      ...list.map((d) => {
        const cmd = d.needsSec ? `${ctx.sec ?? "AAPL"} ${d.code}` : d.code;
        const n = ctx.panel.action(cmd);
        return h(
          "div",
          { class: "row", title: d.help, onclick: (e: MouseEvent) => (e.shiftKey ? ctx.panel.goOther(cmd) : ctx.panel.go(cmd)) },
          h("span", { class: "nm", style: { minWidth: "4ch", textAlign: "right" } }, `${n})`),
          h("span", { class: "code" }, d.code),
          h("span", { class: "amber", style: { minWidth: "22ch" } }, d.name),
          h("span", { class: "dim" }, d.help),
        );
      }),
    );
  });
  clear(
    ctx.body,
    h(
      "div",
      { class: "pad" },
      h("h3", null, "Using BERG"),
      h(
        "div",
        { class: "prose" },
        "Type into the command line and press ",
        h("kbd", null, "Enter"),
        " (the ",
        h("span", { class: "yellow" }, "<GO>"),
        " key). Commands are  SECURITY FUNCTION  — ",
        h("span", { class: "yellow" }, "AAPL GP"),
        ", ",
        h("span", { class: "yellow" }, "SPX Index WEI"),
        ", ",
        h("span", { class: "yellow" }, "CL1 Comdty GP 5Y"),
        ", ",
        h("span", { class: "yellow" }, "EURUSD Curncy DES"),
        ". A ticker alone opens DES; a function alone applies to the loaded security.\n",
        "Numbered rows: type the number and Enter, or click. Shift-click opens in the next panel.\n",
        "Keys: start typing anywhere · ",
        h("kbd", null, "Alt+1..4"),
        " switch panel · ",
        h("kbd", null, "Backspace"),
        " on an empty line or ",
        h("kbd", null, "Alt+←"),
        " goes back · ",
        h("kbd", null, "Tab"),
        " accepts a suggestion · ",
        h("kbd", null, "Esc"),
        " clears · ",
        h("kbd", null, "PgUp/PgDn"),
        " scroll.",
      ),
      h("div", { class: "help-grid", style: { marginTop: "8px" } }, ...sections),
      h("h3", null, "About"),
      h(
        "div",
        { class: "prose dim" },
        "Free public data: Yahoo Finance, SEC EDGAR, US Treasury, FRED, CBOE, Nasdaq, ForexFactory and news RSS feeds. ",
        "Quotes may be delayed; some sources are unofficial and can break. SRC shows source health. ",
        "Watchlists and layout live in your browser only. Not investment advice.",
      ),
    ),
  );
}

// --- SRC --------------------------------------------------------------------

interface SourceStats {
  ok: number;
  err: number;
  lastOk: number | null;
  lastErr: number | null;
  lastError: string | null;
  lastMs: number | null;
}
interface Status {
  uptime: number;
  sources: Record<string, SourceStats>;
  cache: { entries: number; bytes: number };
}

function runSrc(ctx: Ctx): void {
  const head = h("div", { class: "pad" });
  const t = new DataTable<[string, SourceStats]>(
    [
      { label: "Source", get: ([n]) => n, cls: () => "amber" },
      {
        label: "State",
        get: ([, s]) => (s.lastErr && (!s.lastOk || s.lastErr > s.lastOk) ? "FAILING" : "OK"),
        cls: (v: string) => (v === "OK" ? "up" : "down"),
      },
      { label: "OK", get: ([, s]) => s.ok, num: true },
      { label: "Errors", get: ([, s]) => s.err, num: true },
      { label: "Last OK", get: ([, s]) => s.lastOk, fmt: (v) => f.ago(v), num: true },
      { label: "Last error", get: ([, s]) => s.lastErr, fmt: (v) => f.ago(v), num: true },
      { label: "Latency", get: ([, s]) => s.lastMs, fmt: (v: number | null) => (v === null ? "—" : `${v}ms`), num: true },
      { label: "Message", get: ([, s]) => s.lastError ?? "", cls: () => "dim" },
    ],
    { key: ([n]) => n },
  );
  clear(ctx.body, head, t.el);
  const load = async () => {
    const r = await get<Status>("status", {}, ctx.signal);
    const s = r.data;
    clear(
      head,
      `Server up ${Math.floor(s.uptime / 3600)}h${Math.floor((s.uptime % 3600) / 60)}m · cache ${s.cache.entries} entries, ${f.big(s.cache.bytes)}B. `,
      h("span", { class: "dim" }, "Sources appear after first use. Failing sources fall back to the last good copy, marked STALE."),
    );
    t.set(Object.entries(s.sources));
    ctx.panel.status("berg server");
  };
  load().catch((e) => clear(ctx.body, errorBox(e)));
  ctx.panel.every(10_000, load);
}

// --- MOST -------------------------------------------------------------------

const SCREENS: [string, string][] = [
  ["most_actives", "Most active"],
  ["day_gainers", "Gainers"],
  ["day_losers", "Losers"],
  ["small_cap_gainers", "Small-cap gainers"],
  ["most_shorted_stocks", "Most shorted"],
  ["undervalued_growth_stocks", "Undervalued growth"],
  ["growth_technology_stocks", "Growth tech"],
  ["undervalued_large_caps", "Undervalued large caps"],
  ["aggressive_small_caps", "Aggressive small caps"],
];
const MOST_ARGS: Record<string, string> = { ACTIVE: "most_actives", GAINERS: "day_gainers", LOSERS: "day_losers", UP: "day_gainers", DOWN: "day_losers" };

function runMost(ctx: Ctx): void {
  let id = MOST_ARGS[ctx.args[0]] ?? "most_actives";
  const toolbar = h("div", { class: "toolbar" });
  const desc = h("div", { class: "pad dim" });
  const t = new DataTable<Quote>(
    [
      { label: "Ticker", get: (q) => q.symbol, cls: () => "amber" },
      { label: "Name", get: (q) => q.shortName },
      { label: "Last", get: (q) => q.regularMarketPrice, fmt: (v) => f.price(v), num: true },
      { label: "Net chg", get: (q) => q.regularMarketChange, fmt: (v) => f.chg(v), cls: (v) => f.cls(v), num: true },
      { label: "%Chg", get: (q) => q.regularMarketChangePercent, fmt: (v) => f.pct(v), cls: (v) => f.cls(v), num: true },
      { label: "Volume", get: (q) => q.regularMarketVolume, fmt: (v) => f.big(v), num: true },
      { label: "Avg vol 3M", get: (q) => q.averageDailyVolume3Month, fmt: (v) => f.big(v), num: true },
      {
        label: "Rel vol",
        get: (q) => (q.regularMarketVolume && q.averageDailyVolume3Month ? q.regularMarketVolume / q.averageDailyVolume3Month : undefined),
        fmt: (v) => (f.num(v) === undefined ? "—" : `${f.fixedN(v, 1)}x`),
        num: true,
      },
      { label: "Mkt cap", get: (q) => q.marketCap, fmt: (v) => f.big(v), num: true },
      { label: "P/E", get: (q) => q.trailingPE, fmt: (v) => f.fixedN(v, 1), num: true },
      { label: "52w chg", get: (q) => q.fiftyTwoWeekChangePercent, fmt: (v) => f.pct(v, 1), cls: (v) => f.cls(v), num: true },
    ],
    { panel: ctx.panel, key: (q) => q.symbol, action: (q) => `${q.symbol} DES` },
  );
  const drawToolbar = () =>
    clear(
      toolbar,
      ...toggles(SCREENS, id, (v) => {
        id = v;
        drawToolbar();
        clear(desc, loading());
        load();
      }),
    );
  const load = async () => {
    try {
      const r = await get<{ title: string; description: string; quotes: Quote[] }>("screener", { id }, ctx.signal);
      clear(desc, r.data.description ?? "");
      t.set(r.data.quotes);
      ctx.panel.status(`Yahoo screener · ${r.data.title ?? id}`, r.meta);
    } catch (e) {
      if (!ctx.signal.aborted) clear(desc, errorBox(e));
    }
  };
  drawToolbar();
  clear(ctx.body, toolbar, desc, t.el);
  load();
  ctx.panel.every(60_000, load);
}

register(
  {
    code: "HELP",
    name: "Function Directory",
    group: "Tools",
    needsSec: false,
    help: "This list. Also: how commands and keys work.",
    aliases: ["FUNC", "MENU", "?"],
    run: runHelp,
  },
  {
    code: "SRC",
    name: "Data Source Status",
    group: "Tools",
    needsSec: false,
    help: "Health of each upstream data source.",
    aliases: ["STATUS"],
    run: runSrc,
  },
  {
    code: "MOST",
    name: "Most Active / Movers",
    group: "Markets",
    needsSec: false,
    help: "Most active, top gainers and losers, and other Yahoo screens. MOST GAINERS.",
    aliases: ["MOV", "MOVERS"],
    run: runMost,
  },
);
